import { Injectable } from '@angular/core';

/**
 * Centralized deduplication service - industry standard pattern used by Discord, Slack, WhatsApp.
 * Prevents duplicate operations by design rather than ad-hoc fixes.
 */
@Injectable({ providedIn: 'root' })
export class DeduplicationFramework {
  private operationCache = new Map<string, { result: any, timestamp: number, promise?: Promise<any> }>();
  private eventBuffer = new Map<string, { lastEmitted: number, data: any }>();
  private initializationRegistry = new Map<string, boolean>();

  /**
   * Prevent duplicate async operations (Discord uses similar for message fetching)
   * @param key Unique identifier for the operation
   * @param operation The async operation to deduplicate
   * @param ttlMs Time to live in milliseconds (default: 1000ms)
   * @returns The result of the operation (cached or fresh)
   */
  async deduplicateAsync<T>(
    key: string,
    operation: () => Promise<T>,
    ttlMs: number = 1000
  ): Promise<T> {
    const cached = this.operationCache.get(key);
    const now = Date.now();

    if (cached && (now - cached.timestamp) < ttlMs) {
      // Return ongoing promise or cached result
      return cached.promise || cached.result;
    }

    // Start new operation
    const promise = operation();
    this.operationCache.set(key, { result: null, timestamp: now, promise });

    try {
      const result = await promise;
      this.operationCache.set(key, { result, timestamp: Date.now(), promise: undefined });
      return result;
    } catch (error) {
      // Remove failed operation from cache
      this.operationCache.delete(key);
      throw error;
    }
  }

  /**
   * Prevent duplicate sync operations (Slack pattern for DOM operations)
   * @param key Unique identifier for the operation
   * @param operation The sync operation to deduplicate
   * @param ttlMs Time to live in milliseconds (default: 100ms)
   * @returns The result of the operation (cached or fresh)
   */
  deduplicate<T>(
    key: string,
    operation: () => T,
    ttlMs: number = 100
  ): T {
    const cached = this.operationCache.get(key);
    const now = Date.now();

    if (cached && (now - cached.timestamp) < ttlMs) {
      return cached.result;
    }

    const result = operation();
    this.operationCache.set(key, { result, timestamp: now });
    return result;
  }

  /**
   * Event deduplication (WhatsApp Web connection state pattern)
   * @param eventKey Unique identifier for the event
   * @param data Event data
   * @param minIntervalMs Minimum interval between events (default: 10ms)
   * @returns true if event should be emitted, false if it's a duplicate
   */
  deduplicateEvent(
    eventKey: string,
    data: any,
    minIntervalMs: number = 10
  ): boolean {
    const buffered = this.eventBuffer.get(eventKey);
    const now = Date.now();

    if (buffered) {
      const timeSinceLastEmit = now - buffered.lastEmitted;

      // Check if it's too soon to emit again
      if (timeSinceLastEmit < minIntervalMs) {
        return false; // Skip duplicate
      }

      // Check if it's the exact same data (deep equality for objects)
      if (JSON.stringify(buffered.data) === JSON.stringify(data) && timeSinceLastEmit < minIntervalMs * 10) {
        return false; // Skip duplicate with same data
      }
    }

    this.eventBuffer.set(eventKey, { lastEmitted: now, data });
    return true; // Allow emission
  }

  /**
   * Singleton initialization enforcement (Angular/React standard)
   * @param serviceKey Unique identifier for the service
   * @param initFn Initialization function to run once
   */
  ensureSingleInit(serviceKey: string, initFn: () => void): void {
    if (this.initializationRegistry.get(serviceKey)) {
      console.warn(`Duplicate initialization detected for ${serviceKey} - skipping`);
      return;
    }

    this.initializationRegistry.set(serviceKey, true);
    initFn();
    console.log(`Service ${serviceKey} initialized (singleton enforcement)`);
  }

  /**
   * Clear cached operations older than specified age
   * @param maxAgeMs Maximum age in milliseconds
   */
  cleanupCache(maxAgeMs: number = 5000): void {
    const now = Date.now();

    // Clean operation cache
    for (const [key, value] of this.operationCache.entries()) {
      if (now - value.timestamp > maxAgeMs) {
        this.operationCache.delete(key);
      }
    }

    // Clean event buffer
    for (const [key, value] of this.eventBuffer.entries()) {
      if (now - value.lastEmitted > maxAgeMs) {
        this.eventBuffer.delete(key);
      }
    }
  }

  /**
   * Get cache statistics for monitoring
   */
  getCacheStats(): {
    operationCacheSize: number;
    eventBufferSize: number;
    initializedServices: string[];
  } {
    return {
      operationCacheSize: this.operationCache.size,
      eventBufferSize: this.eventBuffer.size,
      initializedServices: Array.from(this.initializationRegistry.keys())
    };
  }

  /**
   * Reset all caches (useful for testing)
   */
  resetAll(): void {
    this.operationCache.clear();
    this.eventBuffer.clear();
    this.initializationRegistry.clear();
  }
}